import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../api/axiosManager';

export const NOTIFICATIONS_PAGE_SIZE = 20;

// Phase 1.3: Status Transition Validation
const STATUS_TRANSITIONS = {
  draft: ['scheduled', 'sending', 'cancelled'],
  scheduled: ['sending', 'cancelled', 'draft'],
  sending: ['sent', 'failed'],
  sent: [], // Final state
  failed: ['sending', 'cancelled'], // Retry veya cancel
  cancelled: [] // Final state
};

// Status geçiş kontrolü
const canTransition = (from, to) => {
  if (!from || !to) return false;
  return STATUS_TRANSITIONS[from]?.includes(to) || false;
};

// Phase 2.1: Cache TTL Configuration
const CACHE_TTL = {
  immediate: 5 * 60 * 1000,      // 5 dakika
  recurring: 10 * 60 * 1000,     // 10 dakika
  'event-based': 15 * 60 * 1000  // 15 dakika
};

// Cache'in yenilenmesi gerekip gerekmediğini kontrol et
const shouldRefetch = (category, lastFetched) => {
  if (!lastFetched) return true;
  const age = Date.now() - lastFetched;
  return age > (CACHE_TTL[category] || 5 * 60 * 1000);
};

// Helper: Backend'den gelen scheduledTime formatını Date objesine çevir
const parseScheduledTime = (scheduledTime) => {
  if (!scheduledTime || scheduledTime === null || scheduledTime === '') {
    return null;
  }

  try {
    // Microsoft JSON date formatını kontrol et: "/Date(1764763200000)/"
    const dateMatch = String(scheduledTime).match(/\/Date\((\d+)\)\//);
    if (dateMatch) {
      const timestamp = parseInt(dateMatch[1]);
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // ISO string formatı: "2025-12-03T15:00:00.000Z" veya "2025-12-03 15:00:00"
    if (typeof scheduledTime === 'string') {
      // "YYYY-MM-DD HH:mm:ss" formatını ISO'ya çevir
      const dateTimeMatch = scheduledTime.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
      if (dateTimeMatch) {
        const [, year, month, day, hours, minutes, seconds] = dateTimeMatch;
        const date = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }

      // ISO string formatı
      const date = new Date(scheduledTime);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Number (timestamp) ise
    if (typeof scheduledTime === 'number') {
      const date = new Date(scheduledTime);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
};

// Tarih dönüştürücü (görüntüleme için)
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // .NET formatı kontrolü
      const timestamp = dateString.match(/\d+/)?.[0];
      if (timestamp) {
        const dotNetDate = new Date(parseInt(timestamp));
        const day = String(dotNetDate.getDate()).padStart(2, '0');
        const month = String(dotNetDate.getMonth() + 1).padStart(2, '0');
        const year = dotNetDate.getFullYear();
        return `${day}.${month}.${year}`;
      }
      return '-';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (error) {
    return '-';
  }
};

// ISO formatına çevir (state'de saklamak için)
const toISOString = (dateString) => {
  if (!dateString) return null;
  try {
    // Zaten ISO formatında mı?
    if (typeof dateString === 'string' && dateString.includes('T')) {
      // ISO formatında olduğunu doğrula
      const testDate = new Date(dateString);
      if (!isNaN(testDate.getTime())) {
        return dateString;
      }
      // Geçersiz ISO string, parse etmeye çalış
    }
    // Formatlanmış string mi? (DD.MM.YYYY) - parse edemeyiz, null dön
    if (typeof dateString === 'string' && dateString.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      return null;
    }
    // Date objesi veya timestamp number
    const date = new Date(dateString);
    // Geçerliliği kontrol et
    if (!isNaN(date.getTime()) && date.getTime() > 0) {
      try {
        return date.toISOString();
      } catch (isoError) {
        return null;
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};

// Helper: Bildirim kategorisini belirle
const getNotificationCategory = (notification) => {
  if (notification.eventType) return 'event-based';
  if (notification.recurringType || notification.scheduledTime) return 'recurring';
  return 'instant';
};

// Tüm bildirimler tek endpoint'e kaydediliyor
const NOTIFICATIONS_BASE_ENDPOINT = '/api/admin/dashboard/notifications';

// Bildirim listesi çek (kategori bazlı)
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (
    {
      category,
      type,
      status,
      search,
      startDate,
      endDate,
      customerId,
      page = 1,
      limit = NOTIFICATIONS_PAGE_SIZE,
      force = false,
      merge = false,
    } = {},
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState().notifications;

      // Phase 2.1: Improved Cache Strategy - TTL and stale-while-revalidate
      const hasActiveFilters = type || status || search || startDate || endDate || customerId;
      const cachedData = state.cache[category];

      if (!force && !hasActiveFilters && cachedData && cachedData.lastFetched) {
        // Cache'in geçerli olup olmadığını kontrol et (TTL)
        const cacheAge = Date.now() - cachedData.lastFetched;
        const ttl = CACHE_TTL[category] || 5 * 60 * 1000;
        const isStale = cacheAge > ttl;

        // Eğer cache hala geçerliyse ve sayfa 1 ise, direkt cache'i dön
        if (!isStale && cachedData.data.length > 0 && page === 1) {
          return {
            notifications: cachedData.data,
            total: cachedData.total,
            page: cachedData.page,
            limit: limit,
            category: category,
            isCached: true
          };
        }

        // Stale-while-revalidate: Eski cache'i göster ama arka planda yenile
        if (isStale && cachedData.data.length > 0 && page === 1 && !cachedData.isRevalidating) {
          // Arka planda revalidation için flag set et (component'te kullanılabilir)
          // Şimdilik eski cache'i dön, revalidation arka planda yapılacak
        }
      }

      const params = new URLSearchParams();
      if (type) params.append('type', type);
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (customerId) params.append('customerId', customerId);
      // Kategori query parametresi olarak gönderiliyor
      if (category) params.append('category', category);
      params.append('page', page);
      params.append('limit', limit);

      // Tüm bildirimler tek endpoint'ten çekiliyor
      const url = `${NOTIFICATIONS_BASE_ENDPOINT}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);

      if (response.data && response.data.success && response.data.data) {

        const mappedNotifications = response.data.data.map(notification => {
          const mapped = {
            id: notification.id || notification._id,
            type: notification.type || 'push',
            title: notification.title || '',
            body: notification.body || '',
            userIds: notification.userIds || [],
            sendTo: notification.sendTo || 'all',
            priority: notification.priority || 'normal',
            channels: notification.channels || ['push'],
            imageUrl: notification.imageUrl || '',
            data: notification.data || {},
            promotionData: notification.promotionData || null,
            status: notification.status || 'draft',
            // scheduledTime, recurringType, recurringTime, eventType alanlarını doğru şekilde map et
            // Backend'den gelen scheduledTime formatını normalize et (parse edip string'e çevir)
            scheduledTime: notification.scheduledTime !== undefined
              ? (() => {
                const parsed = parseScheduledTime(notification.scheduledTime);
                if (parsed && !isNaN(parsed.getTime())) {
                  // "YYYY-MM-DD HH:mm:ss" formatına çevir
                  const year = parsed.getFullYear();
                  const month = String(parsed.getMonth() + 1).padStart(2, '0');
                  const day = String(parsed.getDate()).padStart(2, '0');
                  const hours = String(parsed.getHours()).padStart(2, '0');
                  const minutes = String(parsed.getMinutes()).padStart(2, '0');
                  const seconds = String(parsed.getSeconds()).padStart(2, '0');
                  const formatted = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                  return formatted;
                }
                // Parse edilemezse aynen gönder (zaten doğru formatta olabilir)
                return notification.scheduledTime;
              })()
              : null,
            recurringType: notification.recurringType !== undefined ? notification.recurringType : null,
            recurringTime: notification.recurringTime !== undefined ? notification.recurringTime : null,
            recurringDayOfWeek: notification.recurringDayOfWeek !== undefined ? notification.recurringDayOfWeek : null, // 0-6 arası (0=Pazar, 1=Pazartesi, ..., 6=Cumartesi)
            recurringDayOfMonth: notification.recurringDayOfMonth !== undefined ? notification.recurringDayOfMonth : null, // 1-31 arası veya -1 (ayın son günü)
            eventType: notification.eventType !== undefined ? notification.eventType : null,
            // Backend'den sendCount geliyor, frontend'de sentCount kullanıyoruz
            sentCount: notification.sendCount !== undefined ? notification.sendCount : (notification.sentCount || 0),
            failedCount: notification.failedCount || 0,
            // ISO formatında sakla (timestamp karşılaştırması için), gösterim için component'te formatlanacak
            // Eğer toISOString null dönerse (formatlanmış string gibi), formatDate kullan (gösterim için)
            createdAt: (() => {
              try {
                const iso = toISOString(notification.createdAt);
                return iso || formatDate(notification.createdAt);
              } catch (error) {
                return formatDate(notification.createdAt) || '-';
              }
            })(),
            updatedAt: (() => {
              try {
                const iso = toISOString(notification.updatedAt);
                return iso || formatDate(notification.updatedAt);
              } catch (error) {
                return formatDate(notification.updatedAt) || '-';
              }
            })(),
            sentAt: notification.sentAt ? (() => {
              try {
                const iso = toISOString(notification.sentAt);
                return iso || formatDate(notification.sentAt);
              } catch (error) {
                return formatDate(notification.sentAt) || null;
              }
            })() : null,
            officeCodes: notification.officeCodes || [],
            tahsilatGroups: notification.tahsilatGroups || [],
          };

          return mapped;
        });

        // Pagination bilgisi response.data.pagination içinde geliyor
        const pagination = response.data.pagination || {};
        const responseTotal = pagination.total ?? pagination.totalCount;
        const responsePage = pagination.page ?? page;
        const responseLimit = pagination.limit ?? limit;
        const responseTotalPages =
          pagination.totalPages ??
          (typeof responseTotal === 'number' && responseLimit > 0
            ? Math.ceil(responseTotal / responseLimit)
            : undefined);
        const responseHasNextPage =
          pagination.hasNextPage ??
          (typeof responseTotalPages === 'number' ? responsePage < responseTotalPages : undefined);

        return {
          notifications: mappedNotifications,
          total: responseTotal ?? mappedNotifications.length,
          page: responsePage,
          limit: responseLimit,
          totalPages: responseTotalPages,
          hasNextPage: responseHasNextPage,
        };
      }

      return { notifications: [], total: 0, page: 1, limit: NOTIFICATIONS_PAGE_SIZE };
    } catch (error) {
      // Hata durumunda boş array döndür (state'i temizleme)
      return { notifications: [], total: 0, page: 1, limit: NOTIFICATIONS_PAGE_SIZE };
    }
  }
);

// Bildirim detayı çek (kategori bazlı)
export const fetchNotificationById = createAsyncThunk(
  'notifications/fetchNotificationById',
  async ({ id, category }, { rejectWithValue }) => {
    try {
      // Tüm bildirimler tek endpoint'ten çekiliyor
      const response = await api.get(`${NOTIFICATIONS_BASE_ENDPOINT}/${id}`);

      if (response.data && response.data.success && response.data.data) {
        const notification = response.data.data;
        return {
          id: notification.id || notification._id,
          type: notification.type || 'push',
          title: notification.title || '',
          body: notification.body || '',
          userIds: notification.userIds || [],
          sendTo: notification.sendTo || 'all',
          priority: notification.priority || 'normal',
          channels: notification.channels || ['push'],
          imageUrl: notification.imageUrl || '',
          data: notification.data || {},
          promotionData: notification.promotionData || null,
          status: notification.status || 'draft',
          // Backend'den gelen scheduledTime formatını normalize et
          scheduledTime: notification.scheduledTime !== undefined
            ? (() => {
              const parsed = parseScheduledTime(notification.scheduledTime);
              if (parsed && !isNaN(parsed.getTime())) {
                // "YYYY-MM-DD HH:mm:ss" formatına çevir
                const year = parsed.getFullYear();
                const month = String(parsed.getMonth() + 1).padStart(2, '0');
                const day = String(parsed.getDate()).padStart(2, '0');
                const hours = String(parsed.getHours()).padStart(2, '0');
                const minutes = String(parsed.getMinutes()).padStart(2, '0');
                const seconds = String(parsed.getSeconds()).padStart(2, '0');
                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
              }
              return notification.scheduledTime;
            })()
            : null,
          recurringType: notification.recurringType || null,
          recurringTime: notification.recurringTime || null,
          recurringDayOfWeek: notification.recurringDayOfWeek !== undefined ? notification.recurringDayOfWeek : null,
          recurringDayOfMonth: notification.recurringDayOfMonth !== undefined ? notification.recurringDayOfMonth : null,
          eventType: notification.eventType || null,
          // Backend'den sendCount geliyor, frontend'de sentCount kullanıyoruz
          sentCount: notification.sendCount !== undefined ? notification.sendCount : (notification.sentCount || 0),
          failedCount: notification.failedCount || 0,
          createdAt: formatDate(notification.createdAt),
          updatedAt: formatDate(notification.updatedAt),
          sentAt: formatDate(notification.sentAt),
        };
      }

      return rejectWithValue('Bildirim bulunamadı');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Bildirim detayı yüklenemedi');
    }
  }
);

// Yeni bildirim oluştur (kategori bazlı)
export const createNotification = createAsyncThunk(
  'notifications/createNotification',
  async (notificationData, { rejectWithValue }) => {
    try {
      // Kategoriyi belirle
      let category = 'instant';

      // 1. Event-based kontrolü (en öncelikli)
      if (notificationData.eventType) {
        category = 'event-based';
      }
      // 2. Recurring kontrolü (sadece geçerli recurringType varsa)
      else if (notificationData.recurringType &&
        (notificationData.recurringType === 'daily' ||
          notificationData.recurringType === 'weekly' ||
          notificationData.recurringType === 'monthly')) {
        category = 'recurring';
      }
      // 3. Tek seferlik zamanlanmış (scheduledTime var ama recurringType yok/null/undefined/geçersiz)
      // Tek seferlik zamanlanmış bildirimler instant kategorisine kaydedilmeli
      else if (notificationData.scheduledTime) {
        // recurringType yok, null, undefined, boş string veya geçersiz ise instant
        const hasValidRecurringType = notificationData.recurringType &&
          (notificationData.recurringType === 'daily' ||
            notificationData.recurringType === 'weekly' ||
            notificationData.recurringType === 'monthly');

        if (!hasValidRecurringType) {
          // Tek seferlik zamanlanmış bildirimler instant kategorisine kaydedilmeli
          category = 'instant';
        }
      }

      // Tüm bildirimler tek endpoint'e kaydediliyor
      // Backend API formatına göre hazırla (SP Response formatından INSERT formatına dönüştür)
      const cleanData = {
        type: notificationData.type || 'push',
        title: notificationData.title,
        body: notificationData.body,
        status: notificationData.status || 'draft',
      };

      // Opsiyonel alanlar
      if (notificationData.id) cleanData.id = notificationData.id;
      if (notificationData.imageUrl) cleanData.imageUrl = notificationData.imageUrl;

      // UserIds: String → Array veya Array (SP response'dan gelen string'i array'e çevir)
      if (notificationData.userIds) {
        if (typeof notificationData.userIds === 'string') {
          // Virgülle ayrılmış string ise array'e çevir, değilse aynen gönder
          cleanData.userIds = notificationData.userIds.includes(',')
            ? notificationData.userIds.split(',').map(u => u.trim()).filter(u => u)
            : notificationData.userIds;
        } else if (Array.isArray(notificationData.userIds) && notificationData.userIds.length > 0) {
          cleanData.userIds = notificationData.userIds;
        }
      }

      if (notificationData.sendTo) cleanData.sendTo = notificationData.sendTo;
      if (notificationData.sendTo === 'selected' && notificationData.recipientBatchId) {
        cleanData.recipientBatchId = notificationData.recipientBatchId;
      }

      // Channels: Her kanal ayrı array elemanı olmalı (push_only,websocket → ["push_only", "websocket"])
      if (notificationData.channels) {
        const raw = Array.isArray(notificationData.channels)
          ? notificationData.channels
          : (typeof notificationData.channels === 'string' ? [notificationData.channels] : []);
        const ch = raw.flatMap(c =>
          typeof c === 'string' && c.includes(',')
            ? c.split(',').map(x => x.trim()).filter(Boolean)
            : [c].filter(Boolean)
        );
        if (ch.length > 0) cleanData.channels = [...new Set(ch)];
      }

      if (notificationData.priority) cleanData.priority = notificationData.priority;
      if (notificationData.channelId) cleanData.channelId = notificationData.channelId;
      if (notificationData.officeCode) cleanData.officeCode = notificationData.officeCode;
      if (notificationData.customerId) cleanData.customerId = notificationData.customerId;
      
      // OfficeCodes: Array olarak gönder (mağaza bazlı filtreleme için - sadece sendTo === 'all')
      if (notificationData.officeCodes && notificationData.sendTo === 'all') {
        if (Array.isArray(notificationData.officeCodes) && notificationData.officeCodes.length > 0) {
          cleanData.officeCodes = notificationData.officeCodes;
        } else if (typeof notificationData.officeCodes === 'string') {
          cleanData.officeCodes = notificationData.officeCodes.split(',').map(code => code.trim()).filter(Boolean);
        }
      }

      // Tahsilat kategorileri (sadece sendTo === 'tahsilat')
      if (notificationData.sendTo === 'tahsilat' && Array.isArray(notificationData.tahsilatGroups) && notificationData.tahsilatGroups.length > 0) {
        cleanData.tahsilatGroups = notificationData.tahsilatGroups;
      }

      // Data: JSON String → Object veya Object (SP response'dan gelen JSON string'i object'e çevir)
      if (notificationData.data) {
        if (typeof notificationData.data === 'string') {
          try {
            cleanData.data = JSON.parse(notificationData.data);
          } catch (e) {
            // Parse edilemezse boş object veya atla
          }
        } else if (typeof notificationData.data === 'object' && Object.keys(notificationData.data).length > 0) {
          cleanData.data = notificationData.data;
        }
      }

      // ScheduledTime: ISO8601 formatında gönder (backend ISO8601 bekliyor)
      if (notificationData.scheduledTime) {
        if (typeof notificationData.scheduledTime === 'string') {
          // Microsoft JSON date formatını kontrol et: "/Date(1764763200000)/"
          const dateMatch = notificationData.scheduledTime.match(/\/Date\((\d+)\)\//);
          if (dateMatch) {
            try {
              const timestamp = parseInt(dateMatch[1]);
              const date = new Date(timestamp);

              // ISO8601 formatına çevir (local timezone ile)
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              const seconds = String(date.getSeconds()).padStart(2, '0');
              
              // Timezone offset'i al
              const timezoneOffset = -date.getTimezoneOffset();
              const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
              const offsetMinutes = Math.abs(timezoneOffset) % 60;
              const offsetSign = timezoneOffset >= 0 ? '+' : '-';
              const offsetString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
              
              cleanData.scheduledTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetString}`;
            } catch (e) {
              // Parse edilemezse aynen gönder
              cleanData.scheduledTime = notificationData.scheduledTime;
            }
          } else if (notificationData.scheduledTime.includes('T')) {
            // Zaten ISO8601 formatında ise (T içeriyorsa) aynen gönder
            cleanData.scheduledTime = notificationData.scheduledTime;
          } else {
            // "YYYY-MM-DD HH:mm:ss" formatında ise ISO8601'e çevir
            const dateTimeMatch = notificationData.scheduledTime.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
            if (dateTimeMatch) {
              const [, year, month, day, hours, minutes, seconds] = dateTimeMatch;
              const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(seconds || 0));
              
              // Timezone offset'i al
              const timezoneOffset = -date.getTimezoneOffset();
              const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
              const offsetMinutes = Math.abs(timezoneOffset) % 60;
              const offsetSign = timezoneOffset >= 0 ? '+' : '-';
              const offsetString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
              
              cleanData.scheduledTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds || '00'}${offsetString}`;
            } else {
              // Bilinmeyen format, aynen gönder
              cleanData.scheduledTime = notificationData.scheduledTime;
            }
          }
        } else {
          cleanData.scheduledTime = notificationData.scheduledTime;
        }
      }

      // Timezone: Boş string ise gönderilmez
      if (notificationData.timezone && notificationData.timezone.trim() !== '') {
        cleanData.timezone = notificationData.timezone;
      }

      if (notificationData.recurringType) cleanData.recurringType = notificationData.recurringType;
      if (notificationData.recurringTime) cleanData.recurringTime = notificationData.recurringTime;
      // recurringDayOfWeek: Sadece weekly için gönder
      if (notificationData.recurringType === 'weekly' && notificationData.recurringDayOfWeek !== undefined && notificationData.recurringDayOfWeek !== null) {
        cleanData.recurringDayOfWeek = notificationData.recurringDayOfWeek;
      }
      // recurringDayOfMonth: Sadece monthly için gönder
      if (notificationData.recurringType === 'monthly' && notificationData.recurringDayOfMonth !== undefined && notificationData.recurringDayOfMonth !== null) {
        cleanData.recurringDayOfMonth = notificationData.recurringDayOfMonth;
      }
      if (notificationData.eventType) cleanData.eventType = notificationData.eventType;
      if (notificationData.createdBy) cleanData.createdBy = notificationData.createdBy;

      // Undefined, null ve boş string değerleri temizle
      Object.keys(cleanData).forEach(key => {
        const value = cleanData[key];
        if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
          delete cleanData[key];
        }
        // Boş array'leri de temizle
        if (Array.isArray(value) && value.length === 0) {
          delete cleanData[key];
        }
      });

      // Event-based bildirimler için özel endpoint kullan
      const endpoint = category === 'event-based'
        ? '/api/admin/dashboard/notifications/event-based'
        : NOTIFICATIONS_BASE_ENDPOINT;

      const response = await api.post(endpoint, cleanData);

      if (response.data && response.data.success && response.data.data) {
        const notification = response.data.data;

        // Backend'den gelen status'u öncelikli olarak kullan
        // Backend scheduledTime ile gönderildiğinde otomatik olarak "scheduled" döner
        // Bu yüzden backend'den gelen status'u direkt kullanıyoruz
        const finalStatus = notification.status || 'draft';
        
        // _immediateSend flag'ini notificationData'dan al (backend'e gönderilmedi, ama reducer'da kullanılacak)
        const immediateSendFlag = notificationData._immediateSend || false;

        return {
          id: notification.id || notification._id,
          type: notification.type || notificationData.type,
          title: notification.title || notificationData.title,
          body: notification.body || notificationData.body,
          userIds: notification.userIds || notificationData.userIds || [],
          sendTo: notification.sendTo || notificationData.sendTo || 'all',
          priority: notification.priority || notificationData.priority || 'normal',
          channels: notification.channels || notificationData.channels || ['push'],
          imageUrl: notification.imageUrl || notificationData.imageUrl || '',
          data: notification.data || notificationData.data || {},
          promotionData: notification.promotionData || notificationData.promotionData || null,
          officeCodes: notification.officeCodes || notificationData.officeCodes || [],
          tahsilatGroups: notification.tahsilatGroups || notificationData.tahsilatGroups || [],
          status: finalStatus, // Backend'den gelen status'u direkt kullan
          _immediateSend: immediateSendFlag, // Frontend flag'i (reducer'da kullanılacak)
          // Backend'den gelen scheduledTime formatını normalize et
          scheduledTime: (notification.scheduledTime || notificationData.scheduledTime) !== undefined
            ? (() => {
              const scheduledTimeValue = notification.scheduledTime || notificationData.scheduledTime;
              if (!scheduledTimeValue) return null;
              const parsed = parseScheduledTime(scheduledTimeValue);
              if (parsed && !isNaN(parsed.getTime())) {
                // "YYYY-MM-DD HH:mm:ss" formatına çevir
                const year = parsed.getFullYear();
                const month = String(parsed.getMonth() + 1).padStart(2, '0');
                const day = String(parsed.getDate()).padStart(2, '0');
                const hours = String(parsed.getHours()).padStart(2, '0');
                const minutes = String(parsed.getMinutes()).padStart(2, '0');
                const seconds = String(parsed.getSeconds()).padStart(2, '0');
                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
              }
              return scheduledTimeValue;
            })()
            : null,
          recurringType: notification.recurringType || notificationData.recurringType || null,
          recurringTime: notification.recurringTime || notificationData.recurringTime || null,
          recurringDayOfWeek: notification.recurringDayOfWeek !== undefined ? notification.recurringDayOfWeek : (notificationData.recurringDayOfWeek !== undefined ? notificationData.recurringDayOfWeek : null),
          recurringDayOfMonth: notification.recurringDayOfMonth !== undefined ? notification.recurringDayOfMonth : (notificationData.recurringDayOfMonth !== undefined ? notificationData.recurringDayOfMonth : null),
          eventType: notification.eventType || notificationData.eventType || null,
          // Backend'den sendCount geliyor, frontend'de sentCount kullanıyoruz
          sentCount: notification.sendCount !== undefined ? notification.sendCount : (notification.sentCount || 0),
          failedCount: notification.failedCount || 0,
          createdAt: formatDate(notification.createdAt || new Date()),
          updatedAt: formatDate(notification.updatedAt || new Date()),
          sentAt: notification.sentAt ? formatDate(notification.sentAt) : null,
        };
      }

      return rejectWithValue('Bildirim oluşturulamadı');
    } catch (error) {
      // Validation hatalarını user-friendly mesaj olarak göster
      let errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Bildirim oluşturulamadı';
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const detailMessages = error.response.data.details.map(d => d.msg).join(', ');
        errorMessage = `${errorMessage} (${detailMessages})`;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// scheduledTime'ı ISO 8601 formatına çevir
const toISO8601 = (value) => {
  if (!value) return undefined;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    return value; // Zaten ISO 8601
  }
  const parsed = parseScheduledTime(value);
  if (parsed && !isNaN(parsed.getTime())) {
    return parsed.toISOString();
  }
  return value;
};

// Bildirim güncelle (kategori bazlı)
export const updateNotification = createAsyncThunk(
  'notifications/updateNotification',
  async ({ id, notificationData, category }, { rejectWithValue }) => {
    try {

      // Kategori belirtilmemişse notificationData'dan belirle
      let finalCategory = category;
      if (!finalCategory) {
        if (notificationData.eventType) {
          finalCategory = 'event-based';
        } else if (notificationData.recurringType || notificationData.scheduledTime) {
          finalCategory = 'recurring';
        } else {
          finalCategory = 'instant';
        }
      }

      // API dokümanına göre body: title, body, channels (array), scheduledTime (ISO 8601), userIds, sendTo, imageUrl
      const cleanData = {};

      if (notificationData.title !== undefined && notificationData.title !== null) {
        cleanData.title = notificationData.title;
      }
      if (notificationData.body !== undefined && notificationData.body !== null) {
        cleanData.body = notificationData.body;
      }
      if (notificationData.channels !== undefined && notificationData.channels !== null) {
        const raw = Array.isArray(notificationData.channels)
          ? notificationData.channels
          : [notificationData.channels];
        // Her elemanı virgüle göre böl - "push_only,websocket" -> ["push_only", "websocket"]
        const ch = raw.flatMap(c =>
          typeof c === 'string' && c.includes(',')
            ? c.split(',').map(x => x.trim()).filter(Boolean)
            : [c].filter(Boolean)
        );
        if (ch.length > 0) cleanData.channels = [...new Set(ch)];
      }
      if (notificationData.scheduledTime !== undefined && notificationData.scheduledTime !== null && notificationData.scheduledTime !== '') {
        cleanData.scheduledTime = toISO8601(notificationData.scheduledTime);
      }
      if (notificationData.sendTo !== undefined && notificationData.sendTo !== null) {
        cleanData.sendTo = notificationData.sendTo;
        if (notificationData.sendTo === 'selected' && notificationData.recipientBatchId) {
          cleanData.recipientBatchId = notificationData.recipientBatchId;
        }
        if (notificationData.sendTo === 'selected' && Array.isArray(notificationData.userIds) && notificationData.userIds.length > 0) {
          cleanData.userIds = notificationData.userIds;
        }
        // sendTo: "all" iken userIds gönderme (doc: göndermeyebilir veya [] gönderebilirsiniz)
      }
      if (notificationData.imageUrl !== undefined && notificationData.imageUrl !== null) {
        cleanData.imageUrl = notificationData.imageUrl || null;
      }
      if (notificationData.data) {
        if (typeof notificationData.data === 'string') {
          try {
            cleanData.data = JSON.parse(notificationData.data);
          } catch (e) {
            // Parse edilemezse data gönderme
          }
        } else if (typeof notificationData.data === 'object' && Object.keys(notificationData.data).length > 0) {
          cleanData.data = notificationData.data;
        }
      }
      if (notificationData.officeCodes && Array.isArray(notificationData.officeCodes) && notificationData.officeCodes.length > 0 && notificationData.sendTo === 'all') {
        cleanData.officeCodes = notificationData.officeCodes;
      }
      if (notificationData.sendTo === 'tahsilat' && Array.isArray(notificationData.tahsilatGroups) && notificationData.tahsilatGroups.length > 0) {
        cleanData.tahsilatGroups = notificationData.tahsilatGroups;
      }

      // createNotification ile aynı: tekrarlayan / etkinlik bazlı alanlar güncellemede de gönderilmeli
      if (notificationData.recurringType) cleanData.recurringType = notificationData.recurringType;
      if (
        notificationData.recurringTime !== undefined &&
        notificationData.recurringTime !== null &&
        String(notificationData.recurringTime).trim() !== ''
      ) {
        cleanData.recurringTime = notificationData.recurringTime;
      }
      if (
        notificationData.recurringType === 'weekly' &&
        notificationData.recurringDayOfWeek !== undefined &&
        notificationData.recurringDayOfWeek !== null
      ) {
        cleanData.recurringDayOfWeek = notificationData.recurringDayOfWeek;
      }
      if (
        notificationData.recurringType === 'monthly' &&
        notificationData.recurringDayOfMonth !== undefined &&
        notificationData.recurringDayOfMonth !== null
      ) {
        cleanData.recurringDayOfMonth = notificationData.recurringDayOfMonth;
      }
      if (notificationData.eventType) cleanData.eventType = notificationData.eventType;

      const endpoint = finalCategory === 'event-based'
        ? `/api/admin/dashboard/notifications/event-based/${id}`
        : `${NOTIFICATIONS_BASE_ENDPOINT}/${id}`;

      let response;
      try {
        response = await api.put(endpoint, cleanData);
      } catch (apiError) {
        throw apiError;
      }

      if (response.data && response.data.success && response.data.data) {
        const notification = response.data.data;
        return {
          id: notification.id || id,
          type: notification.type || notificationData.type,
          title: notification.title || notificationData.title,
          body: notification.body || notificationData.body,
          userIds: notification.userIds || notificationData.userIds || [],
          sendTo: notification.sendTo || notificationData.sendTo || 'all',
          priority: notification.priority || notificationData.priority || 'normal',
          channels: notification.channels || notificationData.channels || ['push'],
          imageUrl: notification.imageUrl || notificationData.imageUrl || '',
          data: notification.data || notificationData.data || {},
          promotionData: notification.promotionData || notificationData.promotionData || null,
          status: notification.status || notificationData.status || 'draft',
          // Backend'den gelen scheduledTime formatını normalize et
          scheduledTime: (notification.scheduledTime || notificationData.scheduledTime) !== undefined
            ? (() => {
              const scheduledTimeValue = notification.scheduledTime || notificationData.scheduledTime;
              if (!scheduledTimeValue) return null;
              const parsed = parseScheduledTime(scheduledTimeValue);
              if (parsed && !isNaN(parsed.getTime())) {
                // "YYYY-MM-DD HH:mm:ss" formatına çevir
                const year = parsed.getFullYear();
                const month = String(parsed.getMonth() + 1).padStart(2, '0');
                const day = String(parsed.getDate()).padStart(2, '0');
                const hours = String(parsed.getHours()).padStart(2, '0');
                const minutes = String(parsed.getMinutes()).padStart(2, '0');
                const seconds = String(parsed.getSeconds()).padStart(2, '0');
                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
              }
              return scheduledTimeValue;
            })()
            : null,
          recurringType: notification.recurringType || notificationData.recurringType || null,
          recurringTime: notification.recurringTime || notificationData.recurringTime || null,
          recurringDayOfWeek: notification.recurringDayOfWeek !== undefined ? notification.recurringDayOfWeek : (notificationData.recurringDayOfWeek !== undefined ? notificationData.recurringDayOfWeek : null),
          recurringDayOfMonth: notification.recurringDayOfMonth !== undefined ? notification.recurringDayOfMonth : (notificationData.recurringDayOfMonth !== undefined ? notificationData.recurringDayOfMonth : null),
          eventType: notification.eventType || notificationData.eventType || null,
          officeCodes: notification.officeCodes || notificationData.officeCodes || [],
          tahsilatGroups: notification.tahsilatGroups || notificationData.tahsilatGroups || [],
          // Backend'den sendCount geliyor, frontend'de sentCount kullanıyoruz
          sentCount: notification.sendCount !== undefined ? notification.sendCount : (notification.sentCount || 0),
          failedCount: notification.failedCount || 0,
          createdAt: notification.createdAt ? formatDate(notification.createdAt) : null,
          updatedAt: formatDate(notification.updatedAt || new Date()),
          sentAt: notification.sentAt ? formatDate(notification.sentAt) : null,
        };
      }

      return rejectWithValue('Bildirim güncellenemedi');
    } catch (error) {
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || 'Bildirim güncellenemedi';
      const details = error.response?.data?.details;
      const fullMessage = details && Array.isArray(details) && details.length > 0
        ? `${errorMessage} (${details.map(d => d.msg || d.message || JSON.stringify(d)).join(', ')})`
        : errorMessage;
      return rejectWithValue(fullMessage);
    }
  }
);

// Bildirim sil (kategori bazlı)
export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async ({ id, category }, { rejectWithValue }) => {
    try {
      // Tüm bildirimler tek endpoint'ten siliniyor
      const endpoint = `${NOTIFICATIONS_BASE_ENDPOINT}/${id}`;
      const response = await api.delete(endpoint);
      return id;
    } catch (error) {
      const status = error.response?.status;
      let errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.note ||
        error.userMessage ||
        error.message ||
        'Bildirim silinemedi';

      if (status === 409) {
        errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.note ||
          'Bu bildirim mevcut durumda silinemez. Zamanlanmış bildirimler için önce iptal edin; gönderilmiş bildirimler silinemez.';
      }

      // Validation error details varsa ekle
      if (error.response?.data?.details && Array.isArray(error.response.data.details) && error.response.data.details.length > 0) {
        const detailsMessages = error.response.data.details
          .map(d => d.message || d.msg || JSON.stringify(d))
          .filter(Boolean)
          .join(', ');
        if (detailsMessages) {
          errorMessage = `${errorMessage}${detailsMessages ? ` (${detailsMessages})` : ''}`;
        }
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Bildirimi hemen gönder (kategori bazlı)
export const sendNotification = createAsyncThunk(
  'notifications/sendNotification',
  async ({ id, category }, { rejectWithValue, dispatch }) => {
    try {
      // Tüm bildirimler tek endpoint'ten gönderiliyor
      const endpoint = `${NOTIFICATIONS_BASE_ENDPOINT}/${id}/send`;

      const response = await api.post(endpoint);

      if (response.data && response.data.success && response.data.data) {
        const notification = response.data.data;

        // Gönderim sonuçlarını detaylı logla
        const results = notification.results || {};
        const sendResult = response.data.sendResult || {};

        // Backend'den sendCount geliyor, frontend'de sentCount kullanıyoruz
        // successCount da başarılı gönderim sayısını gösteriyor olabilir
        const sentCount = notification.sendCount !== undefined
          ? notification.sendCount
          : (notification.sentCount !== undefined ? notification.sentCount : 0);

        // Eğer successCount varsa ve sendCount yoksa, successCount'u kullan
        const finalSentCount = sentCount > 0
          ? sentCount
          : (notification.successCount !== undefined ? notification.successCount : 0);

        // Backend'den gelen status'u kontrol et
        const backendStatus = notification.status;

        // Status'u belirle: Backend'den 'sent' geliyorsa onu kullan, yoksa 'sent' varsay
        const finalStatus = backendStatus === 'sent' || backendStatus === 'sending' || backendStatus === 'failed'
          ? backendStatus
          : 'sent'; // Backend status göndermemişse 'sent' varsay

        const returnData = {
          id: notification.id || id,
          status: finalStatus,
          sentCount: finalSentCount,
          failedCount: notification.failedCount !== undefined ? notification.failedCount : 0,
          sentAt: notification.sentAt ? formatDate(notification.sentAt) : formatDate(new Date()),
        };

        return returnData;
      }

      return { id, status: 'sent', sentAt: formatDate(new Date()) };
    } catch (error) {
      // 400 Bad Request hatası için özel mesaj
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || 'Bildirim gönderilemedi';
        const errorDetails = error.response?.data?.details || error.response?.data?.error;

        return rejectWithValue(
          `${errorMessage}${errorDetails ? ` (${JSON.stringify(errorDetails)})` : ''} - Backend loglarını kontrol edin.`
        );
      }

      // 404 hatası için özel mesaj
      if (error.response?.status === 404) {
        return rejectWithValue(
          `Endpoint bulunamadı: /api/admin/dashboard/notifications/${id}/send - Backend route'unun tanımlı olduğundan emin olun.`
        );
      }

      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        'Bildirim gönderilemedi'
      );
    }
  }
);

// Bildirim status güncelle
export const updateNotificationStatus = createAsyncThunk(
  'notifications/updateNotificationStatus',
  async ({ id, statusData }, { rejectWithValue }) => {
    try {
      const endpoint = `${NOTIFICATIONS_BASE_ENDPOINT}/${id}/status`;

      const response = await api.put(endpoint, statusData);

      if (response.data && response.data.success && response.data.data) {
        const notification = response.data.data;
        return {
          id: notification.id || id,
          status: notification.status || statusData.status,
          successCount: notification.successCount || statusData.successCount,
          failedCount: notification.failedCount || statusData.failedCount,
          sendCount: notification.sendCount || statusData.sendCount,
          results: notification.results || statusData.results,
          updatedAt: formatDate(notification.updatedAt || new Date()),
        };
      }

      return rejectWithValue('Status güncellenemedi');
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        'Bildirim status güncellenemedi'
      );
    }
  }
);

// Zamanlanmış bildirimleri getir
export const fetchScheduledNotifications = createAsyncThunk(
  'notifications/fetchScheduledNotifications',
  async (currentTime, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (currentTime) {
        params.append('currentTime', currentTime instanceof Date ? currentTime.toISOString() : currentTime);
      }

      const url = `${NOTIFICATIONS_BASE_ENDPOINT}/scheduled${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);

      if (response.data && response.data.success && response.data.data) {
        const mappedNotifications = response.data.data.map(notification => {
          // Backend'den gelen scheduledTime formatını normalize et
          let normalizedScheduledTime = null;
          if (notification.scheduledTime !== undefined && notification.scheduledTime !== null) {
            const parsed = parseScheduledTime(notification.scheduledTime);
            if (parsed && !isNaN(parsed.getTime())) {
              // "YYYY-MM-DD HH:mm:ss" formatına çevir
              const year = parsed.getFullYear();
              const month = String(parsed.getMonth() + 1).padStart(2, '0');
              const day = String(parsed.getDate()).padStart(2, '0');
              const hours = String(parsed.getHours()).padStart(2, '0');
              const minutes = String(parsed.getMinutes()).padStart(2, '0');
              const seconds = String(parsed.getSeconds()).padStart(2, '0');
              normalizedScheduledTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            } else {
              normalizedScheduledTime = notification.scheduledTime;
            }
          }

          return {
            id: notification.id || notification._id,
            type: notification.type || 'push',
            title: notification.title || '',
            body: notification.body || '',
            status: notification.status || 'scheduled',
            scheduledTime: normalizedScheduledTime,
            createdAt: formatDate(notification.createdAt),
            updatedAt: formatDate(notification.updatedAt),
          };
        });

        return {
          notifications: mappedNotifications,
          count: response.data.count || mappedNotifications.length,
        };
      }

      return { notifications: [], count: 0 };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Zamanlanmış bildirimler yüklenemedi');
    }
  }
);

// Tahsilat kategorilerini çek
export const fetchTahsilatGroups = createAsyncThunk(
  'notifications/fetchTahsilatGroups',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/dashboard/notifications/tahsilat-groups');
      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Tahsilat kategorileri yüklenemedi');
    }
  }
);

// Event Types çek
export const fetchEventTypes = createAsyncThunk(
  'notifications/fetchEventTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/dashboard/notifications/event-types');

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Event types yüklenemedi');
    }
  }
);

export const fetchNotificationStats = createAsyncThunk(
  'notifications/fetchNotificationStats',
  async ({ force = false } = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState().notifications;

      // Cache kontrolü
      if (!force && state.statsCache && state.statsCache.lastFetched) {
        // 5 dakikalık cache süresi (opsiyonel, şimdilik süresiz/manuel invalidation)
        const cacheAge = Date.now() - state.statsCache.lastFetched;
        if (cacheAge < 5 * 60 * 1000) { // 5 dakika
          return { ...state.statsCache.data, isCached: true };
        }
      }

      const response = await api.get('/api/admin/dashboard/notifications/stats');

      if (response.data && response.data.success && response.data.data) {
        const statsData = response.data.data;
        return statsData;
      }

      // Eğer data direkt response.data'da ise
      if (response.data && response.data.success && response.data.total !== undefined) {
        const statsData = response.data;
        return statsData;
      }

      return {
        total: 0,
        byType: {},
        byStatus: {},
        totalSent: 0,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'İstatistikler yüklenemedi');
    }
  }
);

const initialState = {
  notifications: [],
  selectedNotification: null,
  eventTypes: [], // Backend'den gelen event types
  eventTypesLoading: false,
  tahsilatGroups: [], // Tahsilat kategorileri (A0, A1Z1, vb.)
  stats: {
    total: 0,
    byType: {},
    byStatus: {},
  },
  statsCache: {
    data: null,
    lastFetched: null
  },
  pagination: {
    page: 1,
    limit: NOTIFICATIONS_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
  },
  filters: {
    type: '',
    status: '',
    search: '',
  },
  cache: {
    // Kategorilere göre cache - Phase 2.1: TTL ve stale-while-revalidate desteği
    immediate: { data: [], total: 0, page: 1, lastFetched: null, staleAt: null, isRevalidating: false },
    recurring: { data: [], total: 0, page: 1, lastFetched: null, staleAt: null, isRevalidating: false },
    'event-based': { data: [], total: 0, page: 1, lastFetched: null, staleAt: null, isRevalidating: false },
    // Diğer olası kategoriler için dinamik olarak eklenebilir
  },
  // Phase 1.1: Optimistic Updates Tracking
  optimisticUpdates: {}, // { tempId: { type: 'create'|'update'|'delete', notification, timestamp } }
  loading: false,
  statsLoading: false,
  error: null,
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedNotification: (state) => {
      state.selectedNotification = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        type: '',
        status: '',
        search: '',
      };
    },
    // Phase 1.1: Optimistic Updates + Rollback Pattern
    addOptimisticNotification: (state, action) => {
      const { tempId, notification } = action.payload;
      state.optimisticUpdates[tempId] = {
        type: 'create',
        notification: { ...notification },
        timestamp: Date.now()
      };
      // Bildirimi listenin en üstüne ekle
      state.notifications.unshift({ ...notification, id: tempId });
    },
    commitOptimisticUpdate: (state, action) => {
      const { tempId, realId } = action.payload;
      const update = state.optimisticUpdates[tempId];
      if (update) {
        // Temp ID'yi gerçek ID ile değiştir
        const index = state.notifications.findIndex(n => n.id === tempId);
        if (index !== -1) {
          state.notifications[index] = {
            ...state.notifications[index],
            id: realId
          };
        }
        // Optimistic update tracking'den kaldır
        delete state.optimisticUpdates[tempId];
      }
    },
    rollbackOptimisticUpdate: (state, action) => {
      const { tempId } = action.payload;
      const update = state.optimisticUpdates[tempId];
      if (update) {
        // Listeden kaldır
        state.notifications = state.notifications.filter(n => n.id !== tempId);
        // Optimistic update tracking'den kaldır
        delete state.optimisticUpdates[tempId];
      }
    },
    // WebSocket event'lerinden gelen bildirim güncellemeleri için sync reducer
    // Phase 2.2: Timestamp-based conflict resolution ile
    updateNotificationFromWebSocket: (state, action) => {
      const { notificationId, updates, timestamp } = action.payload;
      
      const index = state.notifications.findIndex(n => n.id === notificationId);
      
      if (index !== -1) {
        // Bildirim bulundu, güncelle
        const notification = state.notifications[index];
        
        // Phase 2.2: Timestamp-based conflict resolution
        // updatedAt değeri formatlanmış string olabilir (DD.MM.YYYY) veya ISO formatında olabilir
        let currentTimestamp = 0;
        if (notification.updatedAt) {
          // ISO formatı kontrolü (ISO string veya timestamp number)
          const parsed = new Date(notification.updatedAt).getTime();
          if (!isNaN(parsed)) {
            currentTimestamp = parsed;
          } else {
            // Formatlanmış string (DD.MM.YYYY) ise, timestamp karşılaştırmasını atla
            // Direkt güncelleme yap (çünkü formatlanmış string'den timestamp çıkaramayız)
            currentTimestamp = 0; // Her zaman güncelle (güvenli taraf)
          }
        }
        
        const newTimestamp = timestamp 
          ? new Date(timestamp).getTime() 
          : Date.now();
        
        // Eğer currentTimestamp 0 ise (parse edilemediyse veya yoksa), her zaman güncelle
        // WebSocket event'i geldiğinde güncelleme yap (formatlanmış string'den timestamp çıkaramayız)
        const shouldUpdate = isNaN(newTimestamp) ? false : (currentTimestamp === 0 || newTimestamp > currentTimestamp);
        
        if (shouldUpdate) {
          // WebSocket verisi daha yeni, güncelle
          state.notifications[index] = {
            ...state.notifications[index],
            status: updates.status !== undefined ? updates.status : state.notifications[index].status,
            sendCount: updates.sendCount !== undefined ? updates.sendCount : state.notifications[index].sendCount,
            sentCount: updates.sendCount !== undefined ? updates.sendCount : (updates.sentCount !== undefined ? updates.sentCount : state.notifications[index].sentCount),
            failedCount: updates.failedCount !== undefined ? updates.failedCount : state.notifications[index].failedCount,
            successCount: updates.successCount !== undefined ? updates.successCount : state.notifications[index].successCount,
            updatedAt: timestamp ? (() => {
              try {
                if (typeof timestamp === 'string' && timestamp.includes('T')) {
                  // ISO formatında, geçerliliğini kontrol et
                  const testDate = new Date(timestamp);
                  if (!isNaN(testDate.getTime())) {
                    return timestamp;
                  }
                }
                // Parse et ve ISO'ya çevir
                const date = new Date(timestamp);
                if (!isNaN(date.getTime()) && date.getTime() > 0) {
                  return date.toISOString();
                }
                // Geçersiz timestamp, mevcut değeri koru
                return state.notifications[index].updatedAt;
              } catch (error) {
                return state.notifications[index].updatedAt;
              }
            })() : state.notifications[index].updatedAt,
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Bildirim Listesi
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const args = action.meta?.arg || {};
        const category = action.payload.category || args.category;
        const hasFilters = args.type || args.status || args.search || args.startDate || args.endDate || args.customerId;
        const isForceRefresh = args.force === true;

        // Phase 2.2: Incremental Updates - Force refresh değilse merge stratejisi
        if (action.meta?.arg?.merge || (!isForceRefresh && !hasFilters)) {
          // Merge strategy: Mevcut bildirimleri güncelle, yeni olanları ekle
          const existingIds = new Set(state.notifications.map(n => n.id));
          const newNotifications = [];
          
          action.payload.notifications.forEach(notification => {
            const existingIndex = state.notifications.findIndex(n => n.id === notification.id);
            if (existingIndex !== -1) {
              // Mevcut bildirimi güncelle (incremental update)
              state.notifications[existingIndex] = {
                ...state.notifications[existingIndex],
                ...notification
              };
            } else {
              // Yeni bildirimi ekle
              newNotifications.push(notification);
            }
          });

          // Yeni bildirimleri ekle
          if (newNotifications.length > 0) {
            state.notifications = [...state.notifications, ...newNotifications];
          }
        } else {
          // Force refresh veya filtre var: Tam liste yenileme
          state.notifications = action.payload.notifications;
        }

        // Phase 2.1: Cache güncelleme - TTL ve stale tracking ile
        if (category && !hasFilters) {
          if (!state.cache[category]) {
            state.cache[category] = { 
              data: [], 
              total: 0, 
              page: 1, 
              lastFetched: null, 
              staleAt: null, 
              isRevalidating: false 
            };
          }
          
          const now = Date.now();
          const ttl = CACHE_TTL[category] || 5 * 60 * 1000;
          state.cache[category] = {
            data: action.payload.notifications,
            total: action.payload.total,
            page: action.payload.page,
            lastFetched: now,
            staleAt: now + ttl, // Ne zaman stale olacak
            isRevalidating: false
          };
        }

        // Pagination bilgilerini güncelle
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages:
            action.payload.totalPages ??
            Math.max(1, Math.ceil((action.payload.total || 0) / (action.payload.limit || 1))),
          hasNextPage:
            action.payload.hasNextPage ??
            (action.payload.totalPages
              ? action.payload.page < action.payload.totalPages
              : action.payload.page * action.payload.limit < action.payload.total),
        };
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        // Hata durumunda mevcut bildirimleri koru (state'i temizleme)
        // Sadece error mesajını göster
        if (action.payload) {
          state.error = action.payload;
        }
        // notifications array'ini olduğu gibi bırak (kaybolmasın)
      })

      // Bildirim Detayı
      .addCase(fetchNotificationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedNotification = action.payload;
        state.error = null;
      })
      .addCase(fetchNotificationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bildirim Oluştur
      .addCase(createNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.loading = false;
        const notification = action.payload;
        // Eğer immediate bildirim ise (scheduledTime yok, recurringType yok, eventType yok) status'u "sending" yap
        // Ama eğer payload'da _immediateSend flag'i varsa, status'u direkt "sending" yap
        if (notification._immediateSend || (!notification.scheduledTime && !notification.recurringType && !notification.eventType && notification.status === 'draft')) {
          notification.status = 'sending';
          // _immediateSend flag'ini kaldır (state'e kaydetmeye gerek yok)
          delete notification._immediateSend;
        }
        
        // Phase 1.1: Optimistic update kontrolü - Eğer bildirim zaten varsa (commitOptimisticUpdate'ten sonra) tekrar ekleme
        const existingIndex = state.notifications.findIndex(n => n.id === notification.id);
        if (existingIndex === -1) {
          // Bildirim yok, ekle
          state.notifications.unshift(notification);
        } else {
          // Bildirim zaten var (optimistic update'ten), sadece güncelle
          state.notifications[existingIndex] = {
            ...state.notifications[existingIndex],
            ...notification
          };
        }
        
        state.error = null;
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bildirim Güncelle
      .addCase(updateNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.notifications.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.notifications[index] = action.payload;
        }
        if (state.selectedNotification && state.selectedNotification.id === action.payload.id) {
          state.selectedNotification = action.payload;
        }
        state.error = null;
      })
      .addCase(updateNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bildirim Sil
      .addCase(deleteNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = state.notifications.filter(n => n.id !== action.payload);
        if (state.selectedNotification && state.selectedNotification.id === action.payload) {
          state.selectedNotification = null;
        }
        state.error = null;
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bildirim Gönder
      .addCase(sendNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendNotification.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.notifications.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.notifications[index] = {
            ...state.notifications[index],
            status: action.payload.status || 'sent', // Status'u güncelle
            sentCount: action.payload.sentCount || state.notifications[index].sentCount || 0,
            failedCount: action.payload.failedCount || state.notifications[index].failedCount || 0,
            sentAt: action.payload.sentAt || state.notifications[index].sentAt,
          };
        }
        if (state.selectedNotification && state.selectedNotification.id === action.payload.id) {
          state.selectedNotification = {
            ...state.selectedNotification,
            status: action.payload.status || 'sent',
            sentCount: action.payload.sentCount || state.selectedNotification.sentCount || 0,
            failedCount: action.payload.failedCount || state.selectedNotification.failedCount || 0,
            sentAt: action.payload.sentAt || state.selectedNotification.sentAt,
          };
        }
        state.error = null;
      })
      .addCase(sendNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bildirim Status Güncelle
      .addCase(updateNotificationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { id, status } = action.payload;
        const index = state.notifications.findIndex(n => n.id === id);
        
        if (index !== -1) {
          // Backend zaten geçerli geçişleri kontrol ediyor, bu yüzden frontend'de tekrar kontrol etmeye gerek yok
          // Backend başarılı response dönüyorsa, güncellemeyi yap
          state.notifications[index] = {
            ...state.notifications[index],
            ...action.payload,
          };
        }
        
        if (state.selectedNotification && state.selectedNotification.id === id) {
          state.selectedNotification = {
            ...state.selectedNotification,
            ...action.payload,
          };
        }
        
        state.error = null;
      })
      .addCase(updateNotificationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Event Types
      .addCase(fetchEventTypes.pending, (state) => {
        state.eventTypesLoading = true;
        state.error = null;
      })
      .addCase(fetchEventTypes.fulfilled, (state, action) => {
        state.eventTypesLoading = false;
        state.eventTypes = action.payload;
        state.error = null;
      })
      .addCase(fetchEventTypes.rejected, (state, action) => {
        state.eventTypesLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTahsilatGroups.fulfilled, (state, action) => {
        state.tahsilatGroups = action.payload;
      })
      .addCase(fetchTahsilatGroups.rejected, (state) => {
        state.tahsilatGroups = [];
      })

      // İstatistikler
      .addCase(fetchNotificationStats.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(fetchNotificationStats.fulfilled, (state, action) => {
        state.statsLoading = false;

        // isCached flag'ini temizle (state'e kaydetmeye gerek yok)
        const { isCached, ...statsData } = action.payload;
        state.stats = statsData;

        // Cache'i güncelle (eğer API'den geldiyse)
        if (!isCached) {
          state.statsCache = {
            data: statsData,
            lastFetched: Date.now()
          };
        }

        state.error = null;
      })
      .addCase(fetchNotificationStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload;
      })

      // Zamanlanmış Bildirimler
      .addCase(fetchScheduledNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScheduledNotifications.fulfilled, (state, action) => {
        state.loading = false;
        // Zamanlanmış bildirimleri state'e ekle veya güncelle
        action.payload.notifications.forEach(notification => {
          const index = state.notifications.findIndex(n => n.id === notification.id);
          if (index !== -1) {
            state.notifications[index] = notification;
          } else {
            state.notifications.push(notification);
          }
        });
        state.error = null;
      })
      .addCase(fetchScheduledNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  clearError, 
  clearSelectedNotification, 
  setFilters, 
  clearFilters, 
  updateNotificationFromWebSocket,
  // Phase 1.1: Optimistic Updates
  addOptimisticNotification,
  commitOptimisticUpdate,
  rollbackOptimisticUpdate
} = notificationSlice.actions;
export default notificationSlice.reducer;

